import { ResponseArray, ResponseObject } from '@/types/shared'
import warehouse from '@/utils/axios/warehouse'
import queryString from 'query-string'
import {
    // Response types
    Location,
    Rack,
    Equipment,
    Component,
    Item,
    ItemModel,
    Process,
    Supplier,
    DashboardStats,
    ItemModelOption,
    OwnerOption,
    DepreciationResponse,
    DepreciationHistory,
    // Request types
    CreateProcessRequest,
    UpdateProcessRequest,
    ApproveProcessRequest,
    UpdateStatusRequest,
    DeleteProcessRequest,
    ActivityPayload,
    ActivityType,
    ProcessItemsResponse,
    GetLocationsParams,
    GetRacksParams,
    CreateRackRequest,
    UpdateRackRequest,
    GetEquipmentParams,
    GetComponentsParams,
    GetItemsParams,
    GetProcessesParams,
    DepreciateItemRequest,
    UpdateDepreciationRequest,
    GetModelsParams,
    CreateModelRequest,
    UpdateModelRequest,
    GetSuppliersParams,
    CreateSupplierRequest,
    UpdateSupplierRequest,
    ReturnComponentsRequest,
} from '../types'

// Location APIs
export const getLocations = async (
    params: GetLocationsParams = {}
): Promise<ResponseArray<Location>> => {
    const response = await warehouse.get<ResponseArray<Location>>(
        `/locations?${queryString.stringify(params)}`
    )
    return response.data
}

export const getLocation = async (id: string) => {
    const response = await warehouse.get<ResponseObject<Location>>(
        `/locations/${id}`
    )
    return response.data.result
}

// Rack APIs
export const getRacks = async (
    params: GetRacksParams = {}
): Promise<ResponseArray<Rack>> => {
    const response = await warehouse.get<ResponseArray<Rack>>(
        `/racks?${queryString.stringify(params)}`
    )
    return response.data
}

export const getRack = async (id: string) => {
    const response = await warehouse.get<ResponseObject<Rack>>(`/racks/${id}`)
    return response.data.result
}

export const createRack = async (data: CreateRackRequest): Promise<Rack> => {
    const response = await warehouse.post<ResponseObject<Rack>>('/racks', data)
    return response.data.result
}

export const updateRack = async (
    id: string,
    data: UpdateRackRequest
): Promise<Rack> => {
    const response = await warehouse.put<ResponseObject<Rack>>(
        `/racks/${id}`,
        data
    )
    return response.data.result
}

// Equipment APIs
export const getEquipmentList = async (
    params: GetEquipmentParams = {}
): Promise<ResponseArray<Equipment>> => {
    const response = await warehouse.get<ResponseArray<Equipment>>(
        `/equipment?${queryString.stringify(params)}`
    )
    return response.data
}

export const getEquipment = async (id: string): Promise<Equipment> => {
    const response = await warehouse.get<ResponseObject<Equipment>>(
        `/equipment/${id}`
    )
    return response.data.result
}

// Component APIs
export const getComponents = async (
    params: GetComponentsParams = {}
): Promise<ResponseArray<Component>> => {
    const response = await warehouse.get<ResponseArray<Component>>(
        `/components?${queryString.stringify(params)}`
    )
    return response.data
}

export const getComponent = async (id: string) => {
    const response = await warehouse.get<ResponseObject<Component>>(
        `/components/${id}`
    )
    return response.data.result
}

// Item APIs (unified equipment and components)
export const getItems = async (
    params: GetItemsParams = {}
): Promise<ResponseArray<Item>> => {
    const response = await warehouse.get<ResponseArray<Item>>(
        `/items?${queryString.stringify(params)}`
    )
    return response.data
}

export const getItem = async (id: string) => {
    const response = await warehouse.get<ResponseObject<Item>>(`/items/${id}`)
    return response.data.result
}

// Export APIs — returns ALL rows matching the current filters (no pagination),
// used by the client CSV export. BE hard-caps the result set.
export const getItemsExport = async (
    params: GetItemsParams = {}
): Promise<ResponseArray<Item>> => {
    const response = await warehouse.get<ResponseArray<Item>>(
        `/items/export?${queryString.stringify(params)}`
    )
    return response.data
}

// Process APIs
export const getProcesses = async (
    params: GetProcessesParams = {}
): Promise<ResponseArray<Process>> => {
    const response = await warehouse.get<ResponseArray<Process>>(
        `/processes?${queryString.stringify(params)}`
    )
    return response.data
}

export const getProcess = async (id: string) => {
    const response = await warehouse.get<ResponseObject<Process>>(
        `/processes/${id}`
    )
    return response.data.result
}

// Re-export activity types for backward compatibility
export type { ActivityPayload, ActivityType } from '../types'

export const getProcessLogs = async ({
    process_id,
    params,
}: {
    process_id: string
    params: ActivityPayload
}): Promise<ResponseArray<ActivityType>> => {
    const response = await warehouse.get<ResponseArray<ActivityType>>(
        `/processes/${process_id}/logs?${queryString.stringify(params)}`
    )
    return response.data
}

// Entity-specific logs APIs
export const getLocationLogs = async ({
    location_id,
    params,
}: {
    location_id: string
    params: ActivityPayload
}): Promise<ResponseArray<ActivityType>> => {
    const response = await warehouse.get<ResponseArray<ActivityType>>(
        `/locations/${location_id}/logs?${queryString.stringify(params)}`
    )
    return response.data
}

export const getRackLogs = async ({
    rack_id,
    params,
}: {
    rack_id: string
    params: ActivityPayload
}): Promise<ResponseArray<ActivityType>> => {
    const response = await warehouse.get<ResponseArray<ActivityType>>(
        `/racks/${rack_id}/logs?${queryString.stringify(params)}`
    )
    return response.data
}

export const getEquipmentLogs = async ({
    equipment_id,
    params,
}: {
    equipment_id: string
    params: ActivityPayload
}): Promise<ResponseArray<ActivityType>> => {
    const response = await warehouse.get<ResponseArray<ActivityType>>(
        `/equipment/${equipment_id}/logs?${queryString.stringify(params)}`
    )
    return response.data
}

export const getComponentLogs = async ({
    component_id,
    params,
}: {
    component_id: string
    params: ActivityPayload
}): Promise<ResponseArray<ActivityType>> => {
    const response = await warehouse.get<ResponseArray<ActivityType>>(
        `/components/${component_id}/logs?${queryString.stringify(params)}`
    )
    return response.data
}

export const getProcessItems = async (
    processId: string
): Promise<ProcessItemsResponse> => {
    const response = await warehouse.get<ResponseObject<ProcessItemsResponse>>(
        `/processes/${processId}/items`
    )
    return response.data.result
}

// === Process Mutation APIs ===

// Create a new process
export const createProcess = async (
    data: CreateProcessRequest
): Promise<Process> => {
    const response = await warehouse.post<ResponseObject<Process>>(
        '/processes',
        data
    )
    return response.data.result
}

// Update an existing process
export const updateProcess = async (
    id: string,
    data: UpdateProcessRequest
): Promise<Process> => {
    const response = await warehouse.put<ResponseObject<Process>>(
        `/processes/${id}`,
        data
    )
    return response.data.result
}

// Approve or reject a process
export const approveProcess = async (
    id: string,
    data: ApproveProcessRequest
): Promise<Process> => {
    const response = await warehouse.post<ResponseObject<Process>>(
        `/processes/${id}/approve`,
        data
    )
    return response.data.result
}

// Update process status
export const updateProcessStatus = async (
    id: string,
    data: UpdateStatusRequest
): Promise<Process> => {
    const response = await warehouse.patch<ResponseObject<Process>>(
        `/processes/${id}/status`,
        data
    )
    return response.data.result
}

// Soft delete a process
export const deleteProcess = async (
    id: string,
    data?: DeleteProcessRequest
): Promise<void> => {
    await warehouse.delete(`/processes/${id}`, { data })
}

// Get processes linked to an item (equipment or component)
export const getItemProcesses = async (itemId: string) => {
    const response = await warehouse.get<ResponseArray<Process>>(
        `/items/${itemId}/processes`
    )
    return response.data
}

// === Supplier CRUD APIs ===

export const getSuppliers = async (
    params: GetSuppliersParams = {}
): Promise<ResponseArray<Supplier>> => {
    const response = await warehouse.get<ResponseArray<Supplier>>(
        `/suppliers?${queryString.stringify(params)}`
    )
    return response.data
}

export const getSupplier = async (id: string): Promise<Supplier> => {
    const response = await warehouse.get<ResponseObject<Supplier>>(
        `/suppliers/${id}`
    )
    return response.data.result
}

export const createSupplier = async (
    data: CreateSupplierRequest
): Promise<Supplier> => {
    const response = await warehouse.post<ResponseObject<Supplier>>(
        '/suppliers',
        data
    )
    return response.data.result
}

export const updateSupplier = async (
    id: string,
    data: UpdateSupplierRequest
): Promise<Supplier> => {
    const response = await warehouse.put<ResponseObject<Supplier>>(
        `/suppliers/${id}`,
        data
    )
    return response.data.result
}

export const toggleSupplierActive = async (id: string): Promise<Supplier> => {
    const response = await warehouse.patch<ResponseObject<Supplier>>(
        `/suppliers/${id}/toggle`
    )
    return response.data.result
}

export const deleteSupplier = async (id: string): Promise<void> => {
    await warehouse.delete(`/suppliers/${id}`)
}

// === Item Models and Owners ===

// Get all item models (for dropdown options)
export const getItemModels = async (): Promise<ItemModelOption[]> => {
    const allModels: ItemModel[] = []
    let page = 1
    const pageSize = 100

    // Fetch all pages since backend caps page_size at 100
    while (true) {
        const response = await warehouse.get<ResponseArray<ItemModel>>(
            '/item-models',
            {
                params: { page, page_size: pageSize },
            }
        )

        const models = response.data.results || []
        allModels.push(...models)

        const totalPages = response.data.pagination?.total_pages ?? 1
        if (page >= totalPages) break
        page++
    }

    return allModels.map((m) => ({
        value: m.id,
        label: m.name.toUpperCase(),
        item_type: m.item_type,
        category: m.category.toUpperCase(),
        brand: m.brand,
        specifications: m.specifications,
    }))
}

// === Item Model CRUD APIs ===

// Get paginated list of item models
export const getItemModelsPaginated = async (
    params: GetModelsParams = {}
): Promise<ResponseArray<ItemModel>> => {
    const response = await warehouse.get<ResponseArray<ItemModel>>(
        `/item-models?${queryString.stringify(params)}`
    )
    return response.data
}

// Get single item model by ID
export const getItemModel = async (id: string): Promise<ItemModel> => {
    const response = await warehouse.get<ResponseObject<ItemModel>>(
        `/item-models/${id}`
    )
    return response.data.result
}

// Create a new item model
export const createItemModel = async (
    data: CreateModelRequest
): Promise<ItemModel> => {
    const response = await warehouse.post<ResponseObject<ItemModel>>(
        '/item-models',
        data
    )
    return response.data.result
}

// Update an existing item model
export const updateItemModel = async (
    id: string,
    data: UpdateModelRequest
): Promise<ItemModel> => {
    const response = await warehouse.put<ResponseObject<ItemModel>>(
        `/item-models/${id}`,
        data
    )
    return response.data.result
}

// Toggle item model active status
export const toggleItemModelActive = async (id: string): Promise<ItemModel> => {
    const response = await warehouse.patch<ResponseObject<ItemModel>>(
        `/item-models/${id}/toggle-active`
    )
    return response.data.result
}

// Delete an item model (soft delete)
export const deleteItemModel = async (id: string): Promise<void> => {
    await warehouse.delete(`/item-models/${id}`)
}

// Delete an item (soft delete)
export const deleteItem = async (id: string): Promise<void> => {
    await warehouse.delete(`/items/${id}`)
}

// Return one or more installed components to a warehouse (detach from equipment)
export const returnComponents = async (
    data: ReturnComponentsRequest
): Promise<void> => {
    await warehouse.post('/components/return', data)
}

// Get all distinct owners
export const getOwners = async (): Promise<OwnerOption[]> => {
    const response = await warehouse.get<ResponseArray<OwnerOption>>('/owners')
    return response.data.results || []
}

// Dashboard Stats - Using real API endpoint
export const getDashboardStats = async (): Promise<DashboardStats> => {
    const response =
        await warehouse.get<ResponseObject<DashboardStats>>('/dashboard/stats')
    return response.data.result
}

// === Depreciation APIs ===

/** Apply depreciation calculation to an item */
export const depreciateItem = async (
    itemId: string,
    data: DepreciateItemRequest
): Promise<DepreciationResponse> => {
    const response = await warehouse.post<ResponseObject<DepreciationResponse>>(
        `/items/${itemId}/depreciate`,
        data
    )
    return response.data.result
}

/** Get depreciation history for an item */
export const getDepreciationHistory = async (
    itemId: string
): Promise<DepreciationHistory[]> => {
    const response = await warehouse.get<ResponseArray<DepreciationHistory>>(
        `/items/${itemId}/depreciation-history`
    )
    return response.data.results || []
}

/** Update depreciation rate or current value */
export const updateDepreciation = async (
    itemId: string,
    data: UpdateDepreciationRequest
): Promise<Item> => {
    const response = await warehouse.put<ResponseObject<Item>>(
        `/items/${itemId}/depreciation`,
        data
    )
    return response.data.result
}
